'use client';

import { TrendingUp, TrendingDown, Minus, CircleAlert, ArrowDown } from 'lucide-react';
import { StrategyScoreRing } from '../StrategyScoreRing';
import { SUB_CARD_STYLE } from './shared';
import { MilestoneBar } from './MilestoneBar';
import type { StrategyGoal } from '@/lib/strategy/achievement-meta';

export function StrategyHero({
  score,
  trend,
  prediction,
  goal,
  campaignRunning,
  actionsThisWeek,
  needsAttention = false,
  campaignPaused = false,
  campaignExplicitlyPaused = false,
  embedded = false,
}: {
  score: number;
  trend: number;
  prediction: number;
  goal: StrategyGoal;
  campaignRunning: boolean;
  actionsThisWeek: number;
  /** Low score + a real backlog — the headline flips to an attention state and
   *  points the user down to the "needs you" section instead of "running". */
  needsAttention?: boolean;
  /** At least one engaged automation isn't running (not both apply + outreach
   *  active) — the campaign isn't executing, so the headline says "paused". */
  campaignPaused?: boolean;
  /** True only when a rail the user fully configured was explicitly switched off
   *  (state === 'paused'), as opposed to never having been set up at all. */
  campaignExplicitlyPaused?: boolean;
  /** When true, render without the sub-card chrome (the parent supplies the card). */
  embedded?: boolean;
}) {
  // State-aware summary headline. Distinguishes "explicitly paused" (the user
  // turned a running rail off) from "not yet configured" (rails that were never
  // set up) — the second case gets softer, encouraging copy instead of alarming.
  const headline = campaignPaused
    ? (campaignExplicitlyPaused
        ? 'Your campaign is paused.'
        : 'Your campaign could use a few tweaks.')
    : needsAttention
      ? 'Your campaign needs attention.'
      : campaignRunning
        ? 'Your campaign is running.'
        : "Let's launch your campaign.";
  const flagged = campaignPaused || needsAttention;

  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
  const trendColor = trend > 0 ? 'var(--success-green)' : trend < 0 ? 'var(--alert-red)' : 'var(--muted-foreground)';
  const trendText = `${actionsThisWeek} ${actionsThisWeek === 1 ? 'action' : 'actions'} handled this week`;

  const content = (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-4">
      <StrategyScoreRing score={score} interactive />

      {/* Headline + milestone track + goal → prediction */}
      <div className="flex min-w-[200px] flex-1 flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-title-sm text-foreground">{headline}</span>
          {flagged ? (
            // A green "N actions handled" next to a paused/attention headline reads
            // as a contradiction — point the user down to what they owe instead.
            <span
              className="inline-flex items-center gap-1 text-caption font-semibold"
              style={{ color: 'var(--status-warning)' }}
            >
              <CircleAlert className="h-3 w-3 flex-none" aria-hidden="true" />
              Check what needs you below
              <ArrowDown className="h-3 w-3 flex-none" aria-hidden="true" />
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-body-sm font-bold tabular-nums"
              style={{ color: trendColor }}
            >
              <TrendIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {trendText}
            </span>
          )}
        </div>

        {/* Interactive milestone scrubber: hover/drag to preview a band. */}
        <MilestoneBar score={score} />

        <div className="mt-0.5 text-caption text-muted-foreground">
          Goal: {goal.role} in ~{goal.horizonWeeks} weeks · finish today&rsquo;s plan →{' '}
          <b style={{ color: 'var(--success-green)' }}>{prediction}</b>
        </div>
      </div>
    </div>
  );

  if (embedded) return content;
  return (
    <div className="rounded-lg border p-4" style={SUB_CARD_STYLE}>
      {content}
    </div>
  );
}
